import { useCallback } from 'react';
import { Icon } from '@antimatterhq/ui/components/icon';
import { cn } from '@antimatterhq/ui/lib/utils';

export type Step = {
  id: string;
  name: string;
  completed: boolean;
  clickable: boolean;
  state?: 'none' | 'warning' | 'error' | 'success';
};

export type StepsProps = {
  steps: Step[];
  currentStepId?: string;
  handleStepClick?: (step: Step) => void;
};

type StepState = 'completed' | 'current' | 'incomplete' | 'clickable';

export default function Steps({
  steps,
  currentStepId,
  handleStepClick,
}: StepsProps) {
  const onClick = useCallback(
    (step: Step) => {
      if (handleStepClick && step.clickable) {
        handleStepClick(step);
      }
    },
    [handleStepClick],
  );

  return (
    <ol className="flex w-full flex-col items-start justify-between sm:flex-row sm:items-center">
      {steps.map(({ id, name, completed, clickable, state }, index) => {
        let stepState: StepState = 'incomplete';
        if (id === currentStepId) {
          stepState = 'current';
        } else if (completed) {
          stepState = 'completed';
        } else if (clickable) {
          stepState = 'clickable';
        }
        return (
          <li
            key={id}
            className="mb-4 flex w-full items-center sm:mb-0"
            data-testid={`step-${id}`}
          >
            <div
              className={cn(
                'flex flex-1 items-center',
                clickable && 'cursor-pointer',
              )}
              onClick={() => onClick(steps[index])}
            >
              <div
                className={cn(
                  'mr-2 flex h-[28px] w-[28px] flex-shrink-0 items-center justify-center rounded-full border text-xs font-bold',
                  stepState === 'current' &&
                    'border-blue-600 bg-blue-600 text-white dark:border-blue-500 dark:bg-blue-500 dark:text-white',
                  stepState === 'completed' &&
                    'border-blue-300 bg-grey-50 text-blue-600 dark:border-[rgba(138,202,255,0.16)] dark:bg-[rgb(4,53,93)] dark:text-blue-500',
                  stepState === 'clickable' &&
                    'text-blue-600 dark:text-blue-500',
                  stepState === 'incomplete' &&
                    'border-grey-350 text-grey-350 dark:border-grey-400 dark:text-grey-400',
                )}
                aria-hidden="true"
                data-testid={`step-${id}-circle`}
              >
                {stepState !== 'completed' && index + 1}
                {stepState === 'completed' && (
                  <Icon icon="lucide:check" className="text-base"></Icon>
                )}
              </div>
              <div
                className={cn(
                  'relative whitespace-nowrap text-xs font-bold',
                  stepState === 'incomplete' &&
                    'text-grey-500 dark:text-grey-400',
                  stepState === 'completed' &&
                    'text-blue-600 hover:text-blue-700 dark:text-blue-500 dark:hover:text-blue-400',
                  stepState === 'current' &&
                    'text-blue-600 hover:text-blue-700 dark:text-blue-500 dark:hover:text-blue-400',
                  stepState === 'clickable' &&
                    'text-blue-600 hover:text-blue-700 dark:text-blue-500 dark:hover:text-blue-400',
                )}
                data-testid={`step-${id}-name`}
              >
                {name}
                {state === 'warning' && (
                  <Icon
                    icon="mdi:circle"
                    className="absolute -right-3 -top-2 text-yellow-500"
                    data-testid={`step-${id}-warning`}
                  />
                )}
                {state === 'error' && (
                  <Icon
                    icon="mdi:circle"
                    className="absolute -right-3 -top-2 text-red-500"
                    data-testid={`step-${id}-error`}
                  />
                )}
                {state === 'success' && (
                  <Icon
                    icon="mdi:circle"
                    className="absolute -right-3 -top-2 text-green-500"
                    data-testid={`step-${id}-success`}
                  />
                )}
              </div>
            </div>
            {index < steps.length - 1 && (
              <div
                className="relative mx-2 hidden h-0.5 min-w-[20px] flex-grow sm:block"
                aria-hidden="true"
              >
                <div
                  data-testid={`step-${id}-line`}
                  className={cn(
                    'h-full w-full border-t border-solid',
                    completed && steps[index + 1]?.completed
                      ? 'border-blue-500'
                      : 'border-grey-300 dark:border-grey-650',
                  )}
                ></div>
              </div>
            )}
          </li>
        );
      })}
    </ol>
  );
}
